import * as React from 'react';

import { cn, createTestIdHelper } from '../../../lib/utils';

import { formatBadgeCount } from './format-badge-count';

const testId = createTestIdHelper('notification-badge');

export interface NotificationBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Number of items needing attention. Nothing renders for values <= 0. */
  count: number;
  /** Largest count shown exactly; above it the pill reads `${max}+`. */
  max?: number;
  /**
   * Text announced by assistive technology. Carry the exact count here
   * ("120 unread conversations") even when the pill itself is capped at "99+".
   */
  label: string;
}

/**
 * A small red count pill meaning "N things need attention here" — the counted
 * sibling of `NotificationDot`. Purely presentational: the caller positions it
 * (typically absolutely over an icon via `className`) and decides what it counts.
 *
 * The digits are `text-text-xxs` at weight 600 — heavier than the scale's caption weight
 * (500) on purpose, so one to three white digits stay crisp on the red pill.
 *
 * Colour is red-600 in both modes rather than the `destructive` token: the token
 * is red-500 in light mode, and white 10px text on red-500 falls short of WCAG AA
 * (3.8:1); red-600 clears it (4.8:1) and is the red DESIGN.md names. The ring
 * defaults to the page surface so the pill stays legible over an icon; override
 * `ring-*` on other surfaces (e.g. `ring-sidebar`).
 */
export const NotificationBadge = React.forwardRef<
  HTMLSpanElement,
  NotificationBadgeProps
>(({ count, max, label, className, ...props }, ref) => {
  const text = formatBadgeCount(count, max);
  if (!text) return null;

  return (
    <span
      ref={ref}
      role="status"
      aria-label={label}
      data-testid={testId('root')}
      className={cn(
        'inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-text-xxs font-semibold leading-none tabular-nums text-white ring-2 ring-background',
        className,
      )}
      {...props}
    >
      {text}
    </span>
  );
});

NotificationBadge.displayName = 'NotificationBadge';
